import { API_URL, APP_ID } from '@inbox-demo-nx/app-v2-ondemand-constants';
import { ContextsProvider } from '@inbox-demo-nx/contexts';
import useApp from '@inbox-demo-nx/hooks-use-app';
import { defaultTheme } from '@inbox-demo-nx/theme';
import LoadingFullScreen from '@inbox-demo-nx/ui-loading-full-screen';
import FakeStatusBar from '@inbox-demo-nx/ui-status-bar';
import { ParentMessageTypes } from '@inbox-demo-nx/web-demo-constants';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { makeTheme, View } from 'dripsy';
import * as SplashScreen from 'expo-splash-screen';
import merge from 'lodash/fp/merge';
import React, { useCallback, useMemo } from 'react';
import { Platform, UIManager } from 'react-native';
import { NotifierWrapper } from 'react-native-notifier';

import { scenario } from './constants';
import RootScreen from './screens/RootScreen';

const theme = makeTheme(merge(defaultTheme, {}));

if (typeof window !== 'undefined') {
  // @ts-ignore
  window._frameTimestamp = null;
}

if (Platform.OS === 'android') {
  if (UIManager.setLayoutAnimationEnabledExperimental) {
    UIManager.setLayoutAnimationEnabledExperimental(true);
  }
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 0 } },
});

SplashScreen.preventAutoHideAsync();

const App = () => {
  const {
    authContextValue,
    isLoadingUser,
    isInitialUserLoaded,
    sendbird,
    sendApiRequest,
  } = useApp({
    appId: APP_ID,
    scenario,
    queryClient,
    apiUrl: API_URL,
  });

  const isAppReady = isInitialUserLoaded;
  const sendbirdContextValue = useMemo(
    () => ({ appId: APP_ID, sendbird }),
    [sendbird]
  );

  const onLayoutRootView = useCallback(async () => {
    if (isAppReady) {
      await SplashScreen.hideAsync();
      if (Platform.OS === 'web' && window.self !== window.parent) {
        // the app is in iframe
        window.parent?.postMessage(
          { type: ParentMessageTypes.HIDE_LOADING },
          '*'
        );
      }
    }
  }, [isAppReady]);

  if (!isAppReady) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ContextsProvider
        authContextValue={authContextValue}
        sendbirdContextValue={sendbirdContextValue}
        scenario={scenario}
        sendApiRequest={sendApiRequest}
        theme={theme}
        isDarkTheme={false}
      >
        <NotifierWrapper>
          {Platform.OS === 'web' && <FakeStatusBar variant="dark" />}
          {isLoadingUser ? <LoadingFullScreen /> : <RootScreen />}
          <View onLayout={onLayoutRootView} />
        </NotifierWrapper>
      </ContextsProvider>
    </QueryClientProvider>
  );
};

export default App;
